import React, {useContext, useEffect, useRef, useState} from 'react';
import AddMoneySendArrow from 'components/donate/AddMoneySendArrow';
import {UserContext} from 'components/user/UserContext';
import 'styles/donate/GenericMoney.css';
import {canDonateRequest, postCharityLink} from 'util/api';
import {usePopup} from 'popup/PopupContext';
import {useNavigate} from 'react-router-dom';
import NiceButton from "../pictures/NiceButton";

const AddMoneyHearth = () => {
    const {user} = useContext(UserContext);
    const {closePopup} = usePopup();
    const [popup, setPopup] = useState(false);
    const [copied, setCopied] = useState(true);
    const timeoutRef = useRef(null);
    const resetSendingTimeoutRef = useRef(null);
    const [url, setUrl] = useState('');
    const isLegitAmount = /.+\.[a-zA-Z]{2,}/.test(url);
    const [isSending, setIsSending] = useState(false);
    const inputRef = useRef(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [formattedBadge, setFormattedBadge] = useState('');
    const [canDonate, setCanDonate] = useState(false);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }

        const charityLinks = canDonateRequest();
        charityLinks.then((response) => {
            setLoading(false);
            if (response.value === 'true') {
                setCanDonate(true);
            }
        });

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (resetSendingTimeoutRef.current) clearTimeout(resetSendingTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (user) {
            const badge = user.badgeIndex === 0 ? user.badge : `${user.badge}-${user.badgeIndex}`;
            setFormattedBadge(badge);
        }
    }, [user]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    const createPopup = (copied) => {
        setPopup(true);
        setCopied(copied);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setPopup(false), 1500);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(formattedBadge).then(() => {
            createPopup(true);
        });
    };

    const handleSubmit = async () => {
        if (!isLegitAmount || isSending) return;

        setIsSending(true);
        try {
            await postCharityLink(url);
            navigate("/my-donations");
        } catch (error) {
            if (error.status !== 400) {
                setErrorMessage('Something went wrong. Please try again later or contact us.');
            } else {
                const errorBodyString = await error.body;
                try {
                    const errorBody = JSON.parse(errorBodyString);
                    if (errorBody.length > 0) {
                        setErrorMessage(errorBody[0].errorText);
                    }
                } catch {
                    setErrorMessage('Something went wrong. Please try again later or contact us.');
                }
            }
            resetSendingTimeoutRef.current = setTimeout(() => {
                setIsSending(false);
                setErrorMessage('');
            }, 5000);
        }
    };

    const handlePaste = async () => {
        const text = await navigator.clipboard.readText();
        if (inputRef.current && !isSending) {
            inputRef.current.value = text;
            setUrl(text);
            createPopup(false);
        }
    };

    const handleRedirect = () => {
        navigate('/my-donations');
    };

    /* -------- CONDITIONAL RENDERING -------- */

    if (loading) {
        return <div></div>;
    }

    if (!loading && !canDonate) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <div style={{fontSize: '12px', marginBottom: '10px'}}>
                    Cancel pending donation to create a new one
                </div>
                <div style={{fontSize: '14px'}}>
                    <NiceButton onClick={handleRedirect} text={'Go to My Donations'}/>
                </div>
            </div>
        );
    }

    /* -------- NORMAL UI (loading=false && canDonate=true) -------- */

    return (
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            {popup && (
                <div className="floating-copied-to-clipboard">
                    {copied ? 'Copied to clipboard!' : 'Pasted from clipboard!'}
                </div>
            )}
            <div style={{fontSize: '15px'}}>
                Your badge is{' '}
                <span onClick={handleCopy} className="badge-highlight">
                    {formattedBadge}
                    <img
                        src={'/images/copy-icon.svg'}
                        style={{width: '12px', color: 'green', paddingLeft: '4px'}}
                        alt="url link"
                    />
                </span>.
                Provide url to your charity donation with this badge.
                Amount will be added to your account.
                {errorMessage && (
                    <div style={{
                        color: 'red',
                        fontSize: '12px',
                        marginTop: '2px',
                        display: 'flex',
                        justifyContent: 'center',
                    }}>
                        {errorMessage}
                    </div>
                )}
            </div>
            <span onClick={handleRedirect} className={'go-to-my-donations'}>
                Go to My Donations
            </span>
            <div className="add-money-input-outline love-url">
                <img
                    src={'/images/url-link.svg'}
                    style={{width: '16px', color: 'green', cursor: 'pointer'}}
                    alt="url link"
                    onClick={handlePaste}
                />
                <input
                    className={`add-money-input love-url ${isSending ? 'sending' : ''}`}
                    value={url}
                    onKeyDown={handleKeyDown}
                    ref={inputRef}
                    onChange={(e) => setUrl(e.target.value)}
                    disabled={isSending}
                />
                <AddMoneySendArrow
                    handleSubmit={handleSubmit}
                    isSending={isSending}
                    isLegitAmount={isLegitAmount}
                />
            </div>
        </div>
    );
};

export default AddMoneyHearth;